import {useQuery} from '@tanstack/react-query';
import {Box, CircularProgress, Alert} from '@mui/material';
import apiCall from '../../../../utils/api';
import Section from '../../components/Section';
import MultiIndicator, {MultiIndicatorItem} from '../../components/MultiIndicator';
import {IDashboardData, SurveySlug} from '../../utils/types';
import {buildApiKey} from '../../utils/helpers';

interface Props {
    container: string;
    uoaId: string;
    surveyId: string;
}

const buildSurveySlugs = (surveyId: number): SurveySlug[] => [
    // question3 — Obiettivi delle attività partecipative
    {
        numerator: {
            survey_id: surveyId,
            question: 'question3',
            value: 'Item 1',
        },
        operation: 'match',
        key: 'question3_item-1',
        roman: '',
        title: '',
        tooltip: '',
    },
    {
        numerator: {
            survey_id: surveyId,
            question: 'question3',
            value: 'Item 2',
        },
        operation: 'match',
        key: 'question3_item-2',
        roman: '',
        title: '',
        tooltip: '',
    },
    {
        numerator: {
            survey_id: surveyId,
            question: 'question3',
            value: 'Item 3',
        },
        operation: 'match',
        key: 'question3_item-3',
        roman: '',
        title: '',
        tooltip: '',
    },
    {
        numerator: {
            survey_id: surveyId,
            question: 'question3',
            value: 'Item 4',
        },
        operation: 'match',
        key: 'question3_item-4',
        roman: '',
        title: '',
        tooltip: '',
    },
    {
        numerator: {
            survey_id: surveyId,
            question: 'question3',
            value: 'Item 5',
        },
        operation: 'match',
        key: 'question3_item-5',
        roman: '',
        title: '',
        tooltip: '',
    },
    // question1 — Tipologie di attività partecipative
    {
        numerator: {
            survey_id: surveyId,
            question: 'question1',
            value: 'Item 1',
        },
        operation: 'match',
        key: 'question1_item-1',
        roman: '',
        title: '',
        tooltip: '',
    },
    {
        numerator: {
            survey_id: surveyId,
            question: 'question1',
            value: 'Item 2',
        },
        operation: 'match',
        key: 'question1_item-2',
        roman: '',
        title: '',
        tooltip: '',
    },
    {
        numerator: {
            survey_id: surveyId,
            question: 'question1',
            value: 'Item 3',
        },
        operation: 'match',
        key: 'question1_item-3',
        roman: '',
        title: '',
        tooltip: '',
    },
    {
        numerator: {
            survey_id: surveyId,
            question: 'question1',
            value: 'Item 4',
        },
        operation: 'match',
        key: 'question1_item-4',
        roman: '',
        title: '',
        tooltip: '',
    },
];

const LuoghiDaRigenerare = ({
    container,
    uoaId,
    surveyId,
}: Props) => {
    const {data, isLoading, isError} = useQuery<IDashboardData>({
        queryKey: [
            'luoghi-dashboard4',
            container,
            uoaId,
            surveyId,
        ],
        queryFn: async () => {
            const surveySlugs = buildSurveySlugs(Number(surveyId));

            const res = await apiCall({
                url: `containers/${container}/uoa/${uoaId}/dashboards/fsv`,
                method: 'POST',
                data: {
                    question_slugs: surveySlugs.map((s) => ({
                        numerator: s.numerator,
                        ...(s.denominator && {denominator: s.denominator}),
                        ...(s.operation && {operation: s.operation}),
                    })),
                },
            });

            const result = {} as IDashboardData;
            for (const slug of surveySlugs) {
                const apiKey = buildApiKey(slug);
                result[slug.key] = res.data[apiKey] ?? {uoa: 0, average: 0};
            }
            return result;
        },
        staleTime: 5 * 60 * 1000,
    });

    if (isLoading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', py: 8}}>
                <CircularProgress size={48} />
            </Box>
        );
    }

    if (isError || !data) {
        return <Alert severity='error'>Errore nel caricamento dei dati.</Alert>;
    }

    const obiettiviRows: MultiIndicatorItem[] = [
        {
            label: 'Identificazione/condivisione di usi transitori del bene',
            uoa: data['question3_item-1'].uoa,
            average: data['question3_item-1'].average,
        },
        {
            label: 'Identificazione/condivisione di nuove funzioni/nuovi servizi del bene',
            uoa: data['question3_item-2'].uoa,
            average: data['question3_item-2'].average,
        },
        {
            label: "Condivisione del catalogo/palinsesto di iniziative da realizzare all'interno/esterno del bene",
            uoa: data['question3_item-3'].uoa,
            average: data['question3_item-3'].average,
        },
        {
            label: 'Identificazione/condivisione di strategie di promozione',
            uoa: data['question3_item-4'].uoa,
            average: data['question3_item-4'].average,
        },
        {
            label: 'Altro obiettivo',
            uoa: data['question3_item-5'].uoa,
            average: data['question3_item-5'].average,
        },
    ];

    const tipologiaRows: MultiIndicatorItem[] = [
        {
            label: 'Sondaggi',
            uoa: data['question1_item-1'].uoa,
            average: data['question1_item-1'].average,
        },
        {
            label: 'Singoli incontri aperti alla cittadinanza',
            uoa: data['question1_item-2'].uoa,
            average: data['question1_item-2'].average,
        },
        {
            label: 'Serie di incontri/Seminari rivolti a gruppi di cittadini selezionati',
            uoa: data['question1_item-3'].uoa,
            average: data['question1_item-3'].average,
        },
        {
            label: 'Altre attività',
            uoa: data['question1_item-4'].uoa,
            average: data['question1_item-4'].average,
        },
    ];

    return (
        <Box>
            <Section title='Caratteristiche delle attività partecipative'>
                <MultiIndicator
                    title='Obiettivi delle attività partecipative a confronto con la media'
                    tooltip='Il grafico mostra la distribuzione percentuale delle attività partecipative del vostro progetto per obiettivo indicato dai partecipanti, a confronto con la media della distribuzione per tutte le attività partecipative realizzate in tutti i progetti. La distribuzione media viene calcolata dividendo, per ciascun obiettivo indicato, il numero totale di risposte che lo includono per il numero complessivo di risposte alla domanda.'
                    items={obiettiviRows}
                    maxOverride={100}
                />

                <MultiIndicator
                    title='Tipologie di attività partecipative a confronto con la media'
                    tooltip='Il grafico mostra la distribuzione percentuale delle attività partecipative del vostro progetto per tipologia di attività indicata dai partecipanti, a confronto con la media della distribuzione per tutte le attività partecipative realizzate in tutti i progetti. La distribuzione media viene calcolata dividendo, per ciascuna tipologia indicata, il numero totale di risposte che la includono per il numero complessivo di risposte alla domanda.'
                    items={tipologiaRows}
                    maxOverride={100}
                />
            </Section>
        </Box>
    );
};

export default LuoghiDaRigenerare;
